import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { AuthService } from '../Provider/auth.service';
import { APP_CONSTANTS } from '../constants/app.constants';
import { getNormalizedRoleFromStoredUserData } from '../utils/role-normalization.utils';
import { getDefaultRouteForRole } from '../utils/user-storage.utils';

/** Expired access JWT: refresh via cookie/body; fatal refresh clears session and sends to login. */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (state.url.startsWith(APP_CONSTANTS.ROUTES.STUDENT_ONBOARDING) && route.queryParamMap.has('token')) {
    return true;
  }

  const enforceRoleRoutes = (): boolean => {
    const raw = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.USER_DATA);
    if (!raw) return true;

    let userData: any;
    try {
      userData = JSON.parse(raw);
    } catch {
      return true;
    }

    const roleName = getNormalizedRoleFromStoredUserData(userData);

    if (roleName === APP_CONSTANTS.ROLES.STUDENT) {
      if (!state.url.startsWith(APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD)) {
        router.navigate([APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD]);
        return false;
      }
      return true;
    }

    if (
      state.url.startsWith(APP_CONSTANTS.ROUTES.USERS) &&
      roleName !== APP_CONSTANTS.ROLES.ADMIN &&
      roleName !== APP_CONSTANTS.ROLES.MANAGER &&
      roleName !== APP_CONSTANTS.ROLES.CareerCoach &&
      roleName !== APP_CONSTANTS.ROLES.TechnicalMentor
    ) {
      router.navigate([getDefaultRouteForRole(roleName)]);
      return false;
    }

    return true;
  };

  const token = authService.getToken();
  if (!token) {
    router.navigate([APP_CONSTANTS.ROUTES.LOGIN]);
    return false;
  }

  if (!authService.isTokenExpired(token)) {
    return enforceRoleRoutes();
  }

  return authService.refreshAccessToken().pipe(
    map(() => enforceRoleRoutes()),
    catchError((err) =>
      of(authService.resolveRefreshFailure(err) ? false : enforceRoleRoutes())
    )
  );
};
